/* Shibui Goods — motion layer for Horizon. Deferred; no dependencies.
   1. Reveal: cards and gallery media ease in once their image has actually decoded, so nothing pops in half-drawn.
   2. Tilt: pointer parallax on product cards and on the product-page gallery, with a sheen and a gold rim light.
   3. Touch: the product-page gallery answers to a finger drag, since a phone has no cursor to follow.
   Everything here is decoration: the page works identically without it, and reduced motion turns all of it off. */
(function () {
  "use strict";
  var REDUCE = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var FINE = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  /* ---------------------------------------------------------------- */
  /* Reveal                                                            */
  /* ---------------------------------------------------------------- */
  var io = null;

  function imagesReady(el) {
    var imgs = el.querySelectorAll("img");
    if (!imgs.length) return Promise.resolve();
    var waits = [];
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (im.complete && im.naturalWidth) continue;
      waits.push(new Promise(function (res) {
        var done = function () { res(); };
        im.addEventListener("load", done, { once: true });
        im.addEventListener("error", done, { once: true });
        setTimeout(done, 2500);            // never hold a card hostage to a slow image
      }));
    }
    return Promise.all(waits);
  }

  var stagger = 0, staggerAt = 0;
  function show(el) {
    var now = performance.now();
    if (now - staggerAt > 320) stagger = 0;      // a fresh group starts the count over
    staggerAt = now;
    var d = Math.min(stagger++, 7) * 55;
    imagesReady(el).then(function () {
      setTimeout(function () { el.classList.add("is-in"); }, d);
    });
  }

  function watch(el) {
    if (el.dataset.sgReveal) return;
    el.dataset.sgReveal = "1";
    if (REDUCE || !io) { el.classList.add("is-in"); return; }
    el.classList.add("sg-reveal");
    io.observe(el);
    // Fail-safe: an element that is hidden waiting for a reveal that never fires is worse than no animation at
    // all, so every watched element shows itself after two seconds whatever the observer did or did not report.
    setTimeout(function () { el.classList.add("is-in"); }, 2000);
  }

  /* ---------------------------------------------------------------- */
  /* Tilt                                                              */
  /* ---------------------------------------------------------------- */
  function setVars(host, px, py, max) {
    host.style.setProperty("--sg-ry", ((px - 0.5) * 2 * max).toFixed(2) + "deg");
    host.style.setProperty("--sg-rx", ((0.5 - py) * 2 * max).toFixed(2) + "deg");
    host.style.setProperty("--sg-mx", (px * 100).toFixed(1) + "%");
    host.style.setProperty("--sg-my", (py * 100).toFixed(1) + "%");
  }

  function rest(host) {
    host.style.setProperty("--sg-rx", "0deg");
    host.style.setProperty("--sg-ry", "0deg");
    host.style.setProperty("--sg-mx", "50%");
    host.style.setProperty("--sg-my", "50%");
  }

  function tilt(host, target, max) {
    if (!host || !target || host.dataset.sgTilt) return;
    host.dataset.sgTilt = "1";
    var raf = 0, px = 0.5, py = 0.5;
    function apply() { raf = 0; setVars(host, px, py, max); }
    host.addEventListener("pointermove", function (e) {
      if (e.pointerType !== "mouse") return;
      var r = target.getBoundingClientRect();
      if (!r.width || !r.height) return;
      px = (e.clientX - r.left) / r.width;
      py = (e.clientY - r.top) / r.height;
      host.classList.add("is-tilting");
      if (!raf) raf = requestAnimationFrame(apply);
    });
    host.addEventListener("pointerleave", function () {
      host.classList.remove("is-tilting");
      rest(host);
    });
  }

  /* A finger drag on the product-page media does what a cursor does on desktop: the image leans toward the touch,
     and lets go when the finger does. The drag must not steal a vertical scroll, so only a mostly-horizontal move
     takes over. */
  function dragTilt(host, target, max) {
    if (!host || !target || host.dataset.sgDrag) return;
    host.dataset.sgDrag = "1";
    var x0 = 0, y0 = 0, on = false;
    host.addEventListener("touchstart", function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; on = false;
    }, { passive: true });
    host.addEventListener("touchmove", function (e) {
      if (e.touches.length !== 1) return;
      var t = e.touches[0], dx = t.clientX - x0, dy = t.clientY - y0;
      if (!on && (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy))) return;   // let the page scroll
      on = true;
      var r = target.getBoundingClientRect();
      if (!r.width || !r.height) return;
      host.classList.add("is-tilting");
      setVars(host, Math.min(1, Math.max(0, (t.clientX - r.left) / r.width)),
                    Math.min(1, Math.max(0, (t.clientY - r.top) / r.height)), max);
    }, { passive: true });
    function off() { on = false; host.classList.remove("is-tilting"); rest(host); }
    host.addEventListener("touchend", off, { passive: true });
    host.addEventListener("touchcancel", off, { passive: true });
  }

  /* ---------------------------------------------------------------- */
  function decorateCard(card) {
    var gallery = card.querySelector(".card-gallery");
    if (!gallery) return;
    if (!gallery.querySelector(".sg-sheen")) {
      var s = document.createElement("span");
      s.className = "sg-sheen";
      s.setAttribute("aria-hidden", "true");
      gallery.appendChild(s);
    }
    watch(card);
    if (!REDUCE && FINE) tilt(card, gallery, 6);
  }

  function decorateMedia(m) {
    // The zoom dialog holds its own copies of every slide. They are not on screen, so a reveal would leave them
    // blank when the dialog opens, and clipping them would break the zoom itself.
    if (m.closest("dialog, .dialog-zoomed-gallery, dialog-zoomed-gallery")) return;
    m.classList.add("sg-fx");
    if (!m.querySelector(".sg-rim")) {
      var r = document.createElement("span");
      r.className = "sg-rim";
      r.setAttribute("aria-hidden", "true");
      (m.querySelector(".product-media") || m).appendChild(r);
    }
    watch(m);
    if (REDUCE) return;
    if (FINE) tilt(m, m.querySelector(".product-media") || m, 5);
    else dragTilt(m, m.querySelector(".product-media") || m, 7);
  }

  /* A category tile that reads "Singles 0" looks broken rather than honest. Hide the count while a collection is
     empty; it comes back on its own the moment something is stocked. */
  function pruneZeroCounts(root) {
    root.querySelectorAll(".collection-links__count").forEach(function (c) {
      if (/^\s*0\s*$/.test(c.textContent)) c.classList.add("sg-zero");
      else c.classList.remove("sg-zero");
    });
  }

  function scan(root) {
    root = root || document;
    pruneZeroCounts(root);
    root.querySelectorAll("product-card, .product-card").forEach(decorateCard);
    // Horizon's PDP gallery is a <media-gallery> element; each slide is .product-media-container wrapping .product-media
    root.querySelectorAll("media-gallery .product-media-container").forEach(decorateMedia);
  }

  function start() {
    if (!REDUCE && "IntersectionObserver" in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          show(en.target);
        });
      }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    }
    scan(document);
    // Horizon morphs sections in place (filters, pagination, section rendering). Re-scan on changes, cheaply.
    var pending = 0;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = 0; scan(document); }, 120);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
